import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Link2, Check, X, RefreshCw, AlertCircle, Clock, Mail, Plus, ChevronDown } from "lucide-react";
import { calendarIntegrationsApi } from "../../services/api";
import ConfirmDialog from "../ui/ConfirmDialog";

interface Integration {
  _id: string;
  provider: string;
  status: string;
  email: string;
  displayName: string | null;
  sync: { lastSyncedAt: string | null };
}

interface CalendarIntegrationBannerProps {
  onIntegrationChange?: () => void;
  onStatusChange?: (connected: boolean) => void;
}

const PROVIDERS = [
  {
    id: "google",
    name: "Google Calendar",
    bgColor: "bg-blue-50",
    hoverColor: "hover:bg-blue-100",
    textColor: "text-blue-700",
    borderColor: "border-blue-200",
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    ),
  },
  {
    id: "outlook",
    name: "Microsoft Outlook",
    bgColor: "bg-indigo-50",
    hoverColor: "hover:bg-indigo-100",
    textColor: "text-indigo-700",
    borderColor: "border-indigo-200",
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 22 22" fill="none">
        <path d="M2 6.5C2 5.12 3.12 4 4.5 4H17.5C18.88 4 20 5.12 20 6.5V15.5C20 16.88 18.88 18 17.5 18H4.5C3.12 18 2 16.88 2 15.5V6.5Z" fill="#0078D4"/>
        <path d="M2 6.5L11 12.5L20 6.5" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M2 6.5L11 12.5L20 6.5V15.5C20 16.88 18.88 18 17.5 18H4.5C3.12 18 2 16.88 2 15.5V6.5Z" fill="#0364B8" fillOpacity="0.5"/>
        <path d="M2 6.5L11 12.5L20 6.5" stroke="white" strokeWidth="1.3" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
  },
  {
    id: "apple",
    name: "Apple Calendar",
    bgColor: "bg-slate-100",
    hoverColor: "hover:bg-slate-200",
    textColor: "text-slate-700",
    borderColor: "border-slate-300",
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
    ),
  },
];

const CalendarIntegrationBanner: React.FC<CalendarIntegrationBannerProps> = ({
  onIntegrationChange,
  onStatusChange,
}) => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [comingSoonProvider, setComingSoonProvider] = useState<string | null>(null);
  const [pendingDisconnect, setPendingDisconnect] = useState<Integration | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  const hasSyncedOnMount = useRef(false);

  const fetchIntegrations = async () => {
    try {
      const res = await calendarIntegrationsApi.list();
      const data: Integration[] = res.data?.data || res.data || [];
      setIntegrations(data);
      setError(null);
      onStatusChange?.(data.length > 0);
      return data;
    } catch (err: any) {
      console.error("Failed to fetch calendar integrations:", err);
      if (err?.response?.status !== 404) {
        setError("Failed to load calendar integrations");
      }
      onStatusChange?.(false);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fetch integrations on mount, then auto-sync all connected calendars
  useEffect(() => {
    fetchIntegrations().then((data) => {
      if (hasSyncedOnMount.current || data.length === 0) return;
      hasSyncedOnMount.current = true;
      // Sync all connected integrations in parallel (silently, no spinner)
      Promise.allSettled(
        data.map((i) => calendarIntegrationsApi.sync(i._id))
      ).then(() => {
        fetchIntegrations();
        onIntegrationChange?.();
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = async (provider: string) => {
    // Show "Coming Soon" modal for unsupported providers
    if (provider === "apple") {
      setComingSoonProvider(provider);
      return;
    }

    try {
      setError(null);
      const res = await calendarIntegrationsApi.getAuthUrl(provider);
      const data = res.data?.data || res.data;

      // Apple Calendar uses CalDAV, no OAuth redirect
      if (provider === "apple" && data?.instructions) {
        setError(data.instructions);
        return;
      }

      const authUrl = data?.authUrl;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setError("Could not get authorization URL. Please try again.");
      }
    } catch (err: any) {
      console.error("Failed to get auth URL:", err);
      const serverMsg = err?.response?.data?.error;
      if ((err?.response?.status === 500 || err?.response?.status === 503) && serverMsg?.includes("not configured")) {
        setError(
          "Calendar OAuth credentials are not yet configured. An admin needs to add the provider's API keys to the server environment."
        );
      } else {
        setError("Failed to start calendar connection. Please try again.");
      }
    }
  };

  const handleDisconnect = async (id: string) => {
    setDisconnecting(true);
    try {
      setError(null);
      await calendarIntegrationsApi.disconnect(id);
      await fetchIntegrations();
      onIntegrationChange?.();
    } catch (err) {
      console.error("Failed to disconnect integration:", err);
      setError("Failed to disconnect calendar. Please try again.");
    } finally {
      setDisconnecting(false);
      setPendingDisconnect(null);
    }
  };

  const handleSync = async (id: string) => {
    try {
      setError(null);
      setSyncingId(id);
      await calendarIntegrationsApi.sync(id);
      await fetchIntegrations();
      onIntegrationChange?.();
    } catch (err) {
      console.error("Failed to sync integration:", err);
      setError("Failed to sync calendar. Please try again.");
    } finally {
      setSyncingId(null);
    }
  };

  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!addMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
        setAddMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [addMenuOpen]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-600" />
          <span className="text-sm text-slate-500">Loading integrations...</span>
        </div>
      </div>
    );
  }

  return (
    <>
    <AnimatePresence mode="wait">
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="flex items-center gap-2 px-4 py-3 mb-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto p-1 rounded-lg hover:bg-red-100 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      )}

      {integrations.length === 0 ? (
        /* ── No integrations: Show connect banner ── */
        <motion.div
          key="connect-banner"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="bg-gradient-to-r from-slate-50 to-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            {/* Icon + Text */}
            <div className="flex items-start gap-4 flex-1">
              <div
                className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "rgba(15, 81, 50, 0.1)" }}
              >
                <Calendar className="h-6 w-6" style={{ color: "var(--color-primary)" }} />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">
                  Connect your calendar
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Sync your Google Calendar, Outlook, or Apple Calendar to see all
                  your events in one place.
                </p>
              </div>
            </div>

            {/* Provider buttons */}
            <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
              {PROVIDERS.map((provider) => (
                <button
                  key={provider.id}
                  onClick={() => handleConnect(provider.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${provider.bgColor} ${provider.hoverColor} ${provider.textColor} ${provider.borderColor}`}
                >
                  {provider.icon}
                  {provider.name}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      ) : (
        /* ── Has integrations: Show connected status bar ── */
        <motion.div
          key="connected-bar"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 border-l-4 p-4 mb-6"
          style={{ borderLeftColor: "var(--color-accent)" }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Connected integrations */}
            <div className="flex flex-wrap items-center gap-3 flex-1">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <Link2 className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                <span>Connected:</span>
              </div>

              {integrations.map((integration) => {
                const providerInfo = PROVIDERS.find(
                  (p) => p.id === integration.provider
                );
                const isSyncing = syncingId === integration._id;

                return (
                  <div
                    key={integration._id}
                    className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 border border-slate-200"
                  >
                    {/* Green check */}
                    <div className="h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-accent)" }}>
                      <Check className="h-3 w-3 text-white" />
                    </div>

                    {/* Provider name + email */}
                    <span className="text-sm font-medium text-slate-700">
                      {providerInfo?.name || integration.provider}
                    </span>
                    <span className="text-xs text-slate-500">
                      {integration.email}
                    </span>

                    {/* Sync button */}
                    <button
                      onClick={() => handleSync(integration._id)}
                      disabled={isSyncing}
                      className="p-1 rounded-lg hover:bg-slate-200 transition-colors text-slate-400 hover:text-slate-600 disabled:opacity-50"
                      title="Sync Now"
                    >
                      <RefreshCw
                        className={`h-3.5 w-3.5 ${isSyncing ? "animate-spin" : ""}`}
                      />
                    </button>

                    {/* Disconnect button */}
                    <button
                      onClick={() => setPendingDisconnect(integration)}
                      className="p-1 rounded-lg hover:bg-red-100 transition-colors text-slate-400 hover:text-red-600"
                      title="Disconnect"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}

              {/* Syncing status indicator */}
              {syncingId && (
                <span className="text-xs text-slate-500 animate-pulse">
                  Syncing...
                </span>
              )}
            </div>

            {/* Connect another calendar dropdown */}
            <div className="relative flex-shrink-0" ref={addMenuRef}>
              <button
                onClick={() => setAddMenuOpen((prev) => !prev)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-600 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Connect Calendar</span>
                <span className="sm:hidden">Add</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${addMenuOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {addMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 4, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.97 }}
                    transition={{ duration: 0.12 }}
                    className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-1.5 z-50"
                  >
                    <div className="px-3 py-2 border-b border-slate-100">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Connect a calendar</span>
                    </div>
                    {PROVIDERS.map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => {
                          setAddMenuOpen(false);
                          handleConnect(provider.id);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${provider.bgColor} border ${provider.borderColor}`}>
                          {provider.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-700">{provider.name}</div>
                          {provider.id === "apple" && (
                            <div className="text-xs text-slate-400">Coming soon</div>
                          )}
                          {(provider.id === "google" || provider.id === "outlook") && integrations.some((i) => i.provider === provider.id) && (
                            <div className="text-xs text-slate-400">Add another account</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Last synced info */}
          {integrations.some((i) => i.sync?.lastSyncedAt) && (
            <div className="mt-2 text-xs text-slate-400">
              Last synced:{" "}
              {integrations
                .filter((i) => i.sync?.lastSyncedAt)
                .map((i) => {
                  const providerInfo = PROVIDERS.find(
                    (p) => p.id === i.provider
                  );
                  return (
                    <span key={i._id} className="mr-3">
                      {providerInfo?.name || i.provider}{" "}
                      {new Date(i.sync.lastSyncedAt!).toLocaleString([], {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  );
                })}
            </div>
          )}
        </motion.div>
      )}
      {/* Coming Soon Modal */}
      {comingSoonProvider && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setComingSoonProvider(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 max-w-md mx-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "rgba(15, 81, 50, 0.1)" }}>
              <Clock className="h-7 w-7" style={{ color: "var(--color-primary)" }} />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">
              Apple Calendar Integration Coming Soon
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              We're working on bringing Apple Calendar integration to Kavoa. In the meantime, you can connect your Google Calendar or Microsoft Outlook.
            </p>
            <div className="flex flex-col gap-3">
              <a
                href="mailto:support@kavoa.io?subject=I'd like Apple Calendar integration ASAP&body=Hi Kavoa team,%0A%0AI'd love to have Apple Calendar integration as soon as possible.%0A%0AThanks!"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors text-sm"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--color-primary-hover)"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "var(--color-primary)"}
              >
                <Mail className="h-4 w-4" />
                Let us know you want it ASAP
              </a>
              <button
                onClick={() => setComingSoonProvider(null)}
                className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    <ConfirmDialog
      open={!!pendingDisconnect}
      title="Disconnect calendar?"
      message={`Events will stop syncing with ${pendingDisconnect?.email || "this calendar"}. You can reconnect it any time.`}
      confirmLabel="Disconnect"
      variant="danger"
      loading={disconnecting}
      onConfirm={() => pendingDisconnect && handleDisconnect(pendingDisconnect._id)}
      onCancel={() => !disconnecting && setPendingDisconnect(null)}
    />
    </>
  );
};

export default CalendarIntegrationBanner;
